import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Plus, Pencil, Trash2, ToggleLeft, ToggleRight,
  Search, X, Check, AlertTriangle, Loader2, Shield, Mail,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { APP_CONFIG } from '../config/appConfig';
import { authFetch } from '../config/authConfig';

interface RoleSummary {
  id: number;
  nombre: string;
  estado: string;
}

interface AppUser {
  id: string;
  nombre: string;
  apellido: string;
  email: string;
  estado: string;
  departamento: string;
  roles: RoleSummary[];
  ultimo_login?: string | null;
  fecha_registro: string | null;
  fecha_actualizacion: string | null;
}

const API_BASE = APP_CONFIG.BACKEND_URL;

export default function UsersPage() {
  const { user } = useAuth();
  const { addNotification } = useNotifications();

  const [users, setUsers] = useState<AppUser[]>([]);
  const [availableRoles, setAvailableRoles] = useState<RoleSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<AppUser | null>(null);
  const [formName, setFormName] = useState('');
  const [formLastName, setFormLastName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formDepartamento, setFormDepartamento] = useState('Apoyo Financiero');
  const [formRoles, setFormRoles] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [emailError, setEmailError] = useState('');

  const [confirmDialog, setConfirmDialog] = useState<{
    type: 'delete' | 'toggle';
    targetUser: AppUser;
  } | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes] = await Promise.all([
        authFetch(`${API_BASE}/api/v1/users`),
        authFetch(`${API_BASE}/api/v1/roles`),
      ]);
      if (usersRes.ok) setUsers(await usersRes.json());
      if (rolesRes.ok) {
        const allRoles = await rolesRes.json();
        setAvailableRoles(allRoles.filter((r: any) => r.estado === 'activo'));
      }
    } catch {
      addNotification({ type: 'error', title: 'Error', message: 'No se pudieron cargar los usuarios' });
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredUsers = users.filter(u =>
    `${u.nombre} ${u.apellido || ''}`.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  function validateEmail(email: string): boolean {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) { setEmailError('El correo es requerido'); return false; }
    if (!trimmed.endsWith('@uniandes.edu.co')) {
      setEmailError('El correo debe ser @uniandes.edu.co');
      return false;
    }
    const regex = /^[a-zA-Z0-9_.+-]+@uniandes\.edu\.co$/;
    if (!regex.test(trimmed)) {
      setEmailError('Formato de correo inválido');
      return false;
    }
    setEmailError('');
    return true;
  }

  function openCreate() {
    setEditingUser(null);
    setFormName('');
    setFormLastName('');
    setFormEmail('');
    setFormDepartamento('Apoyo Financiero');
    setFormRoles(new Set());
    setEmailError('');
    setShowForm(true);
  }

  function openEdit(u: AppUser) {
    setEditingUser(u);
    setFormName(u.nombre);
    setFormLastName(u.apellido || '');
    setFormEmail(u.email);
    setFormDepartamento(u.departamento || 'Apoyo Financiero');
    setFormRoles(new Set(u.roles.map(r => r.id)));
    setEmailError('');
    setShowForm(true);
  }

  function toggleRole(id: number) {
    setFormRoles(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function handleSave() {
    if (!formName.trim()) {
      addNotification({ type: 'warning', title: 'Validación', message: 'El nombre es requerido' });
      return;
    }
    if (!formLastName.trim()) {
      addNotification({ type: 'warning', title: 'Validación', message: 'El apellido es requerido' });
      return;
    }
    if (!validateEmail(formEmail)) return;
    if (formRoles.size === 0) {
      addNotification({ type: 'warning', title: 'Validación', message: 'Debe seleccionar al menos un rol' });
      return;
    }

    setSaving(true);
    try {
      const body = {
        nombre: formName.trim(),
        apellido: formLastName.trim(),
        email: formEmail.trim().toLowerCase(),
        role_ids: Array.from(formRoles),
        departamento: formDepartamento.trim(),
        usuario_id: user?.id || '',
        usuario_nombre: user?.name || '',
      };

      const url = editingUser
        ? `${API_BASE}/api/v1/users/${editingUser.id}`
        : `${API_BASE}/api/v1/users`;

      const res = await authFetch(url, {
        method: editingUser ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Error al guardar');
      }

      addNotification({
        type: 'success', title: 'Éxito',
        message: editingUser ? 'Usuario actualizado correctamente' : 'Usuario creado correctamente',
        autoClose: true,
      });
      setShowForm(false);
      fetchData();
    } catch (e: any) {
      addNotification({ type: 'error', title: 'Error', message: e.message });
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleEstado(targetUser: AppUser) {
    const nuevoEstado = targetUser.estado === 'activo' ? 'inactivo' : 'activo';
    try {
      const res = await authFetch(`${API_BASE}/api/v1/users/${targetUser.id}/estado`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          estado: nuevoEstado,
          usuario_id: user?.id || '',
          usuario_nombre: user?.name || '',
        }),
      });
      if (!res.ok) throw new Error('Error al cambiar estado');
      addNotification({
        type: 'success', title: 'Éxito',
        message: `Usuario ${nuevoEstado === 'activo' ? 'activado' : 'inactivado'} correctamente`,
        autoClose: true,
      });
      fetchData();
    } catch (e: any) {
      addNotification({ type: 'error', title: 'Error', message: e.message });
    }
    setConfirmDialog(null);
  }

  async function handleDelete(targetUser: AppUser) {
    try {
      const res = await authFetch(
        `${API_BASE}/api/v1/users/${targetUser.id}?usuario_id=${encodeURIComponent(user?.id || '')}&usuario_nombre=${encodeURIComponent(user?.name || '')}`,
        { method: 'DELETE' },
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Error al eliminar');
      }
      addNotification({
        type: 'success', title: 'Éxito', message: 'Usuario eliminado correctamente', autoClose: true,
      });
      fetchData();
    } catch (e: any) {
      addNotification({ type: 'error', title: 'Error', message: e.message });
    }
    setConfirmDialog(null);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Usuarios</h1>
          <p className="text-sm text-gray-500 mt-1">Administra los usuarios y sus roles en el sistema.</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
          style={{ backgroundColor: '#1B2A4A' }}
        >
          <Plus className="w-4 h-4" /> Nuevo Usuario
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar por nombre o correo..."
          className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent"
          style={{ '--tw-ring-color': '#EDAA00' } as React.CSSProperties}
        />
      </div>

      {/* Users table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        {filteredUsers.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No se encontraron usuarios</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Usuario</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Correo</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Roles</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Estado</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredUsers.map(u => (
                  <tr key={u.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold text-white flex-shrink-0"
                          style={{ backgroundColor: u.estado === 'activo' ? '#1B2A4A' : '#9CA3AF' }}
                        >
                          {(u.nombre?.[0] || '?')}{(u.apellido?.[0] || '')}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{u.nombre} {u.apellido || ''}</p>
                          {u.departamento && (
                            <p className="text-xs text-gray-400">{u.departamento}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <Mail className="w-3.5 h-3.5 text-gray-400" />
                        {u.email}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap gap-1">
                        {u.roles.map(r => (
                          <span
                            key={r.id}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full bg-indigo-50 text-indigo-600"
                          >
                            <Shield className="w-3 h-3" /> {r.nombre}
                          </span>
                        ))}
                        {u.roles.length === 0 && (
                          <span className="text-xs text-gray-400 italic">Sin roles asignados</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                        u.estado === 'activo' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'
                      }`}>
                        {u.estado === 'activo' ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(u)}
                          className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                          title="Editar"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setConfirmDialog({ type: 'toggle', targetUser: u })}
                          className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                          title={u.estado === 'activo' ? 'Inactivar' : 'Activar'}
                        >
                          {u.estado === 'activo'
                            ? <ToggleRight className="w-4 h-4 text-green-500" />
                            : <ToggleLeft className="w-4 h-4" />
                          }
                        </button>
                        <button
                          onClick={() => setConfirmDialog({ type: 'delete', targetUser: u })}
                          className="p-2 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600 transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Form Modal */}
      {createPortal(
      <AnimatePresence>
        {showForm && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-[9998]"
              onClick={() => setShowForm(false)}
            />
            <div className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-[560px] max-h-[85vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden pointer-events-auto"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900">
                  {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
                </h2>
                <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg hover:bg-gray-100">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1">Nombre *</label>
                    <input
                      value={formName}
                      onChange={e => setFormName(e.target.value)}
                      placeholder="Nombre"
                      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent"
                      style={{ '--tw-ring-color': '#EDAA00' } as React.CSSProperties}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1">Apellido *</label>
                    <input
                      value={formLastName}
                      onChange={e => setFormLastName(e.target.value)}
                      placeholder="Apellido"
                      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent"
                      style={{ '--tw-ring-color': '#EDAA00' } as React.CSSProperties}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1">Departamento</label>
                    <input
                      value={formDepartamento}
                      onChange={e => setFormDepartamento(e.target.value)}
                      placeholder="Ej: Apoyo Financiero"
                      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent"
                      style={{ '--tw-ring-color': '#EDAA00' } as React.CSSProperties}
                    />
                  </div>
                  <div />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Correo electrónico *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      value={formEmail}
                      onChange={e => { setFormEmail(e.target.value); setEmailError(''); }}
                      onBlur={() => formEmail && validateEmail(formEmail)}
                      placeholder="usuario@uniandes.edu.co"
                      className={`w-full pl-10 pr-4 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent ${
                        emailError ? 'border-red-300' : 'border-gray-200'
                      }`}
                      style={{ '--tw-ring-color': '#EDAA00' } as React.CSSProperties}
                    />
                  </div>
                  {emailError && (
                    <p className="text-xs text-red-500 mt-1">{emailError}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">Solo se permiten correos @uniandes.edu.co</p>
                </div>

                {/* Roles */}
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Roles *</label>
                  <div className="space-y-2">
                    {availableRoles.map(role => (
                      <label
                        key={role.id}
                        className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-gray-200 cursor-pointer transition-all"
                      >
                        <button
                          onClick={() => toggleRole(role.id)}
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                            formRoles.has(role.id) ? 'border-transparent text-white' : 'border-gray-300'
                          }`}
                          style={formRoles.has(role.id) ? { backgroundColor: '#EDAA00' } : {}}
                        >
                          {formRoles.has(role.id) && <Check className="w-3 h-3" />}
                        </button>
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-indigo-500" />
                          <span className="text-sm font-medium text-gray-800">{role.nombre}</span>
                        </div>
                      </label>
                    ))}
                    {availableRoles.length === 0 && (
                      <p className="text-sm text-gray-400 italic">No hay roles disponibles</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
                <button
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl transition-all hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: '#1B2A4A' }}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {editingUser ? 'Guardar Cambios' : 'Crear Usuario'}
                </button>
              </div>
            </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>,
      document.body)}

      {/* Confirm Dialog */}
      {createPortal(
      <AnimatePresence>
        {confirmDialog && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-[9998]"
              onClick={() => setConfirmDialog(null)}
            />
            <div className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-[420px] bg-white rounded-2xl shadow-2xl p-6 pointer-events-auto"
            >
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  confirmDialog.type === 'delete' ? 'bg-red-100' : 'bg-amber-100'
                }`}>
                  <AlertTriangle className={`w-5 h-5 ${
                    confirmDialog.type === 'delete' ? 'text-red-600' : 'text-amber-600'
                  }`} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {confirmDialog.type === 'delete' ? 'Eliminar usuario' : (
                      confirmDialog.targetUser.estado === 'activo' ? 'Inactivar usuario' : 'Activar usuario'
                    )}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {confirmDialog.type === 'delete'
                      ? `¿Estás seguro de que deseas eliminar a "${confirmDialog.targetUser.nombre} ${confirmDialog.targetUser.apellido || ''}"? El usuario será desactivado y no podrá acceder al sistema.`
                      : `¿Deseas ${confirmDialog.targetUser.estado === 'activo' ? 'inactivar' : 'activar'} a "${confirmDialog.targetUser.nombre} ${confirmDialog.targetUser.apellido || ''}"?`
                    }
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setConfirmDialog(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => confirmDialog.type === 'delete'
                    ? handleDelete(confirmDialog.targetUser)
                    : handleToggleEstado(confirmDialog.targetUser)
                  }
                  className={`px-4 py-2 text-sm font-semibold text-white rounded-xl transition-all hover:opacity-90 ${
                    confirmDialog.type === 'delete' ? 'bg-red-600' : ''
                  }`}
                  style={confirmDialog.type !== 'delete' ? { backgroundColor: '#1B2A4A' } : {}}
                >
                  {confirmDialog.type === 'delete' ? 'Eliminar' : 'Confirmar'}
                </button>
              </div>
            </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>,
      document.body)}
    </div>
  );
}
